import { DailyPnlItem } from '../../../../../apis/idea/idea-api.def.ts'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { LineChart } from '../../../../../common/echarts/line-chart.tsx'
import { EChartsOption, SeriesOption } from 'echarts'
import dayjs from 'dayjs'
import { formatCurrency } from '../../../../../utils/formatters.ts'
import { useTranslation } from 'react-i18next'

interface PnlLineChartProps {
  data: DailyPnlItem[]
  currency: string
}

export const IdeaPnlLineChart: React.FC<PnlLineChartProps> = ({
  data,
  currency,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)

  const lineSeries: SeriesOption = {
    type: 'line',
    data: data.map((item) => ({
      value: item.pnl,
      name: dayjs(item.positionDate).format('YYYY-MM-DD'),
      raw: item, // Attach the full item for tooltip access
    })),
    areaStyle: {},
  }

  const lineChartOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const point = Array.isArray(params) ? params[0] : params
        const item = (point.data as { raw?: DailyPnlItem })?.raw
        if (!item) return ''
        return `
          <div style="
            border-radius: 8px;
            //border: 1px solid ${themeStyle.colorBorder};
            //box-shadow: 1px 1px 6px 0px rgba(0, 0, 0, 0.23);
            padding: 8px 12px;
          ">
            <div style="margin-bottom: 8px; font-size: 16px; font-weight: 500;"><b>${dayjs(
              item.positionDate
            ).format('YYYY/MM/DD')}</b></div>
            <table style="width:100%;border-collapse:collapse;">
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.shares')}</td>
                <td style="text-align:right; font-weight: 600">${item.shares.toLocaleString()}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.avg_cost')}</td>
                <td style="text-align:right; font-weight: 600;">${formatCurrency(item.avgCost, { currency: currency })}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.eod_price')}</td>
                <td style="text-align:right; font-weight: 600;">${formatCurrency(item.eodPrice, { currency: currency })}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.fx_rate')}</td>
                <td style="text-align:right; font-weight: 600;">${item.fxRate.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 2 }
                )}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t(
                  'idea_list.idea_modal.idea_pnl_line_chart.realized_pnl'
                )}</td>
                <td style="text-align:right; font-weight: 600;">$${item.realizedPnl.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 0 }
                )}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t(
                  'idea_list.idea_modal.idea_pnl_line_chart.unrealized_pnl'
                )}</td>
                <td style="text-align:right; font-weight: 600;">$${item.unrealizedPnl.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 0 }
                )}</td>
              </tr>
            </table>
          </div>
        `
      },
    },
    xAxis: {
      type: 'category',
      data: data.map((item) => dayjs(item.positionDate).format('YYYY/MM/DD')),
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => `$${value.toLocaleString()}`,
        margin: 16,
      },
    },
    series: [lineSeries],
  }

  return (
    <div
      style={{
        padding: `${themeStyle.paddingXXS} ${themeStyle.paddingMD}`,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '8px',
        border: `1px solid ${themeStyle.sumCardOutline}`,
        background: themeStyle.sumCardBg,
        backdropFilter: 'blur(4px)',
      }}
    >
      <LineChart series={lineSeries} chartOptions={lineChartOptions} />
    </div>
  )
}
